/* eslint-disable react/prop-types */
/* global process */
import React, { useState, useEffect, useMemo } from 'react';
import api from '../../utils/api';
import { useUser } from '../../contexts/UserContext';
import toast from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Select from 'react-select';
import {
    FiFileText, FiCheckCircle, FiClock, FiUsers, FiTrash2, FiEye,
    FiPlus, FiFolder, FiCheck, FiFilter, FiDownload, FiTag,
    FiEdit, FiSearch, FiX
} from 'react-icons/fi';
import DataTable from 'react-data-table-component';

const priorityColors = {
    'Normal': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', badge: 'bg-blue-100 text-blue-800' },
    'Important': { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-200', badge: 'bg-orange-100 text-orange-800' },
    'Critical': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', badge: 'bg-red-100 text-red-800' }
};

const quillModules = {
    toolbar: [
        ['bold', 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link'],
    ],
};

const stripHtml = (html = '') => html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

const customStyles = {
    headRow: {
        style: {
            backgroundColor: "#f9fafb",
            color: "#374151",
            fontWeight: "600",
            fontSize: "0.75rem",
            borderBottom: "2px solid #e5e7eb",
            minHeight: "45px",
        },
    },
    headCells: {
        style: {
            paddingLeft: "16px",
            paddingRight: "16px",
            fontWeight: "bold",
            textTransform: "uppercase",
            fontSize: "0.75rem",
            color: "#4b5563"
        },
    },
    cells: {
        style: {
            paddingLeft: "16px",
            paddingRight: "16px",
            fontSize: "0.75rem",
            paddingTop: "12px",
            paddingBottom: "12px",
        },
    },
    rows: {
        style: {
            minHeight: "60px",
            borderBottom: "1px solid #e5e7eb",
            "&:hover": {
                backgroundColor: "#f9fafb",
            },
        },
    },
};

function CustomPagination({ currentPage, rowCount, rowsPerPage, onChangePage }) {
    if (!rowCount || rowCount === 0) return null;
    const totalPages = Math.max(1, Math.ceil(rowCount / rowsPerPage));
    const start = (currentPage - 1) * rowsPerPage + 1;
    const end = Math.min(currentPage * rowsPerPage, rowCount);

    const getPageNumbers = () => {
        const pages = [];
        const isMobile = window.innerWidth < 640;
        const maxVisible = isMobile ? 3 : 5;
        let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
        let endPage = Math.min(totalPages, startPage + maxVisible - 1);
        if (endPage - startPage + 1 < maxVisible) {
            startPage = Math.max(1, endPage - maxVisible + 1);
        }
        startPage = Math.max(1, startPage);
        for (let i = startPage; i <= endPage; i++) {
            pages.push(i);
        }
        return pages;
    };

    return (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-3 bg-gray-50 border-t border-gray-100 w-full">
            <div className="text-xs text-gray-500 order-2 sm:order-1">
                Showing {start}–{end} of {rowCount}
            </div>
            <div className="flex gap-1 sm:gap-2 items-center order-1 sm:order-2">
                <button
                    type="button"
                    onClick={() => onChangePage(1)}
                    disabled={currentPage <= 1}
                    className="px-2 py-1 text-xs border border-gray-200 rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 bg-white min-w-[30px] h-[30px] flex items-center justify-center font-medium"
                >«</button>
                {getPageNumbers().map((pageNum) => (
                    <button
                        key={pageNum}
                        type="button"
                        onClick={() => onChangePage(pageNum)}
                        className={`min-w-[30px] h-[30px] flex items-center justify-center text-xs border rounded font-medium transition-all ${currentPage === pageNum
                            ? "bg-main text-white border-main shadow-sm"
                            : "border-gray-200 bg-white text-gray-600 hover:bg-gray-50"
                            }`}
                    >{pageNum}</button>
                ))}
                <button
                    type="button"
                    onClick={() => onChangePage(totalPages)}
                    disabled={currentPage >= totalPages}
                    className="px-2 py-1 text-xs border border-gray-200 rounded disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 bg-white min-w-[30px] h-[30px] flex items-center justify-center font-medium"
                >»</button>
            </div>
        </div>
    );
}



export default function HrNotes() {
    const { user } = useUser();
    const roleId = parseInt(user?.user_role_id ?? user?.role_id ?? 0);
    const isHROrAdmin = roleId === 1;

    // Active sub-tab for HR: 'manage' or 'tracking'
    const [hrTab, setHrTab] = useState('manage');

    return (
        <div className="w-full">
            {isHROrAdmin ? (
                <div className="space-y-6">
                    {/* Sub tabs for HR */}
                    <div className="flex border-b border-gray-200 bg-white p-2 rounded-lg shadow-sm">
                        <button
                            onClick={() => setHrTab('manage')}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md transition-colors ${hrTab === 'manage'
                                ? 'bg-main text-white'
                                : 'text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            <FiFileText /> HR Notes Management
                        </button>
                        <button
                            onClick={() => setHrTab('tracking')}
                            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-md transition-colors ${hrTab === 'tracking'
                                ? 'bg-main text-white'
                                : 'text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            <FiUsers /> Acknowledgement Tracking
                        </button>
                    </div>

                    {hrTab === 'manage' ? <HrNotesManagement /> : <HrAcknowledgementTracking />}
                </div>
            ) : (
                <EmployeeNotesView />
            )}
        </div>
    );
}

// =========================================================================
// SUB-COMPONENT: HR Notes Management (Form, Summary Cards & List)
// =========================================================================
function HrNotesManagement() {
    const [summary, setSummary] = useState({
        total_notes: 0,
        active_notes: 0,
        pending_acknowledgements: 0,
        acknowledged_users: 0
    });
    const [notesList, setNotesList] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Search / sort / pagination
    const [searchText, setSearchText] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // Form modal state
    const [showFormModal, setShowFormModal] = useState(false);

    // Preview note modal state
    const [previewNote, setPreviewNote] = useState(null);

    // Custom confirm delete modal
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    // Form state
    const [formData, setFormData] = useState({
        id: null,
        title: '',
        description: '',
        priority: '',
        message_type: '',
        category: '',
        effective_date: new Date().toISOString().split('T')[0],
        expiry_date: '',
        require_acknowledgement: 1,
        existingAttachmentName: null,
        existingAttachmentPath: null,
        deleteExistingAttachment: false
    });
    const [attachment, setAttachment] = useState(null);

    const fetchSummary = async () => {
        try {
            const res = await api.get('/hrms/notes/summary');
            if (res.data.status) setSummary(res.data.data);
        } catch (e) { console.error('Error fetching notes summary:', e); }
    };

    const fetchNotes = async () => {
        try {
            const res = await api.get('/hrms/notes/list');
            if (res.data.status) setNotesList(res.data.data);
        } catch (e) { console.error('Error fetching notes list:', e); }
    };

    const fetchEmployees = async () => {
        try {
            const res = await api.get('/hrms/employee-list');
            if (res.data.status) {
                const list = res.data.data
                    .filter(emp => parseInt(emp.account_status ?? emp.status ?? 0) === 1)
                    .map(emp => ({
                        value: emp.user_id,
                        label: `${emp.first_name} ${emp.last_name} (${emp.designation || 'Employee'})`
                    }));
                setEmployees(list);
            }
        } catch (e) { console.error('Error fetching employees list:', e); }
    };

    const loadData = async () => {
        setLoading(true);
        await Promise.all([fetchSummary(), fetchNotes(), fetchEmployees()]);
        setLoading(false);
    };

    useEffect(() => {
        loadData();
    }, []);

    const handleFormSubmit = async (statusVal) => {
        if (!formData.title.trim()) return toast.error('Note Title is required');
        if (!stripHtml(formData.description)) return toast.error('Note Description is required');
        if (!formData.priority) return toast.error('Priority is required');
        if (!formData.message_type) return toast.error('Message Type is required');
        if (!formData.category) return toast.error('Category is required');
        if (formData.message_type === 'Individual Employee' && (!selectedEmployee || (Array.isArray(selectedEmployee) && selectedEmployee.length === 0))) {
            return toast.error('Please select at least one employee for individual message');
        }
        if (!formData.effective_date) return toast.error('Effective Date is required');

        setSubmitting(true);
        try {
            const payload = new FormData();
            if (formData.id) payload.append('id', formData.id);
            payload.append('title', formData.title);
            payload.append('description', formData.description);
            payload.append('priority', formData.priority);
            payload.append('message_type', formData.message_type);
            payload.append('category', formData.category || 'memo');
            payload.append('effective_date', formData.effective_date);
            payload.append('expiry_date', formData.expiry_date || '');
            payload.append('require_acknowledgement', formData.require_acknowledgement);
            payload.append('status', statusVal); // 'Published' or 'Draft'

            if (formData.message_type === 'Individual Employee' && selectedEmployee) {
                const ids = Array.isArray(selectedEmployee)
                    ? selectedEmployee.map(emp => emp.value).join(',')
                    : selectedEmployee.value;
                payload.append('recipient_user_ids', ids);
            }
            if (attachment) {
                payload.append('attachment', attachment);
            }
            if (formData.deleteExistingAttachment) {
                payload.append('delete_attachment', 'true');
            }

            const url = formData.id ? '/hrms/notes/update' : '/hrms/notes/create';
            const res = await api.post(url, payload, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });

            if (res.data.status) {
                toast.success(formData.id ? 'HR Note updated successfully!' : 'HR Note published successfully!');
                resetForm();
                setShowFormModal(false);
                loadData();
            } else {
                toast.error(res.data.message || 'Operation failed');
            }
        } catch (e) {
            console.error('Submit error:', e);
            toast.error('An error occurred during submission.');
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            id: null,
            title: '',
            description: '',
            priority: '',
            message_type: '',
            category: '',
            effective_date: new Date().toISOString().split('T')[0],
            expiry_date: '',
            require_acknowledgement: 1,
            existingAttachmentName: null,
            existingAttachmentPath: null,
            deleteExistingAttachment: false
        });
        setSelectedEmployee(null);
        setAttachment(null);
        // Reset file input element if exists
        const fileInput = document.getElementById('note-attachment-input');
        if (fileInput) fileInput.value = '';
    };

    const handleDelete = async (noteId) => {
        setConfirmDeleteId(noteId);
    };

    const confirmDelete = async () => {
        const noteId = confirmDeleteId;
        setConfirmDeleteId(null);
        try {
            const res = await api.post('/hrms/notes/delete', { id: noteId });
            if (res.data.status) {
                toast.success('Note deleted successfully');
                loadData();
            } else {
                toast.error(res.data.message || 'Failed to delete note');
            }
        } catch (e) {
            console.error('Delete error:', e);
            toast.error('Error occurred while deleting note.');
        }
    };

    const handleEditNote = (note) => {
        setFormData({
            id: note.id,
            title: note.title,
            description: note.description,
            priority: note.priority,
            message_type: note.message_type,
            category: note.category || 'memo',
            effective_date: note.effective_date,
            expiry_date: note.expiry_date || '',
            require_acknowledgement: parseInt(note.require_acknowledgement) === 1 ? 1 : 0,
            existingAttachmentName: note.attachment_name,
            existingAttachmentPath: note.attachment_path,
            deleteExistingAttachment: false
        });
        if (note.message_type === 'Individual Employee' && note.recipient_user_id) {
            const recipientIds = String(note.recipient_user_id).split(',').map(id => parseInt(id.trim()));
            const selectedEmps = employees.filter(e => recipientIds.includes(parseInt(e.value)));
            setSelectedEmployee(selectedEmps.length > 0 ? selectedEmps : null);
        } else {
            setSelectedEmployee(null);
        }
        setShowFormModal(true);
    };


    const filteredNotes = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        return notesList.filter((row) => {
            if (!query) return true;
            return ['title', 'message_type', 'priority', 'status', 'author_name'].some((field) =>
                String(row[field] ?? '').toLowerCase().includes(query)
            );
        });
    }, [notesList, searchText]);

    const columns = useMemo(() => [
        {
            name: 'Note Title',
            selector: row => row.title,
            sortable: true,
            minWidth: '150px',
            grow: 1,
            cell: row => (
                <div className="font-medium text-gray-800 py-2">
                    <div className="truncate font-semibold text-gray-800 text-xs" title={row.title}>{row.title}</div>
                    {row.message_type === 'Individual Employee' && (
                        <div className="text-[10px] text-indigo-600 font-semibold mt-0.5">👤 Personal Message</div>
                    )}
                </div>
            )
        },
        {
            name: 'Priority',
            selector: row => row.priority,
            sortable: true,
            minWidth: '150px',
            grow: 1,
            cell: row => {
                const pColor = priorityColors[row.priority] || priorityColors['Normal'];
                return (
                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${pColor.badge}`}>
                        {row.priority}
                    </span>
                );
            }
        },
        {
            name: 'Message Type',
            selector: row => row.message_type,
            sortable: true,
            minWidth: '150px',
            grow: 1,
            cell: row => <span className="text-gray-600 text-xs">{row.message_type}</span>
        },
        {
            name: 'Published Date',
            selector: row => row.created_at,
            sortable: true,
            minWidth: '150px',
            grow: 1,
            cell: row => <span className="text-gray-600 text-xs">{new Date(row.created_at).toLocaleDateString('en-GB')}</span>
        },
        {
            name: 'Publish Status',
            selector: row => row.status,
            sortable: true,
            minWidth: '150px',
            grow: 1,
            cell: row => (
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold border transition-all duration-200 cursor-default ${row.status === 'Published'
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 hover:text-emerald-800 hover:border-emerald-300'
                    : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100 hover:text-gray-800 hover:border-gray-300'
                    }`}>
                    {row.status}
                </span>
            )
        },
        {
            name: 'Status',
            selector: row => row.read_count,
            sortable: true,
            minWidth: '150px',
            grow: 1,
            cell: row => (
                <span className="text-xs font-bold text-gray-700 flex items-center gap-1">
                    <span className="text-blue-600" title="Read Count">R: {row.read_count}</span>
                    <span className="text-gray-300">|</span>
                    <span className="text-black" title="Acknowledged Count">A: {row.acknowledged_count}</span>
                    <span className="text-gray-400 font-normal text-[10px] ml-1">(Recip: {row.total_recipients})</span>
                </span>
            )
        },
        {
            name: 'Actions',
            minWidth: '150px',
            grow: 1,
            center: true,
            cell: row => (
                <div className="flex items-center justify-center gap-1.5">
                    {row.status === 'Published' && (
                        <button onClick={() => setPreviewNote(row)} className="p-1.5 text-indigo-600 hover:bg-indigo-50 rounded transition" title="Preview Note (Replay)"><FiEye /></button>
                    )}
                    <button onClick={() => handleEditNote(row)} className="p-1.5 text-orange-600 hover:bg-orange-50 rounded transition" title="Edit Note"><FiEdit /></button>
                    <button onClick={() => handleDelete(row.id)} className="p-1.5 text-red-600 hover:bg-red-50 rounded transition" title="Delete Note"><FiTrash2 /></button>
                </div>
            )
        }
    ], [employees]);

    if (loading && notesList.length === 0) {
        return (
            <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-main"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Notes List */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex flex-col sm:flex-row items-start sm:items-center gap-3 justify-between">
                    <h2 className="font-bold text-gray-800 flex items-center gap-2">
                        <FiFolder className="text-main" /> HR Announcements &amp; Notes
                    </h2>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                            <span>Show</span>
                            <select
                                value={rowsPerPage}
                                onChange={(e) => setRowsPerPage(Number(e.target.value))}
                                className="bg-white border border-gray-200 rounded-lg pl-2 pr-6 py-1 text-xs font-bold text-gray-700 focus:ring-1 focus:ring-main outline-none cursor-pointer min-w-[55px]"
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                                <option value={100}>100</option>
                            </select>
                            <span>entries</span>
                        </div>
                        <div className="relative">
                            <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                            <input
                                type="text"
                                value={searchText}
                                onChange={e => setSearchText(e.target.value)}
                                placeholder="Search notes..."
                                className="pl-8 pr-7 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-main w-44"
                            />
                            {searchText && <button onClick={() => setSearchText('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><FiX size={12} /></button>}
                        </div>
                        <button
                            onClick={() => { resetForm(); setShowFormModal(true); }}
                            className="flex items-center gap-1.5 px-4 py-2 bg-main hover:bg-mainHover text-white font-bold rounded-lg text-xs transition"
                        >
                            <FiPlus /> Create HR Note
                        </button>
                    </div>
                </div>
                <div className="border-t border-gray-100">
                    <DataTable
                        columns={columns}
                        data={filteredNotes}
                        customStyles={customStyles}
                        pagination
                        paginationComponent={CustomPagination}
                        paginationPerPage={rowsPerPage}
                        paginationRowsPerPageOptions={[5, 10, 25, 50, 100]}
                        highlightOnHover
                        noDataComponent={
                            <div className="px-6 py-8 text-center text-gray-500">
                                {searchText ? 'No notes match your search.' : 'No HR Notes created yet.'}
                            </div>
                        }
                    />
                </div>
            </div>

            {/* Create / Edit Note Form Popup Modal */}
            {showFormModal && (
                <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden text-sm animate-fade-in">

                        {/* Header */}
                        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-gray-50">
                            <h3 className="font-extrabold text-gray-800 text-lg flex items-center gap-2">
                                {formData.id ? 'Edit HR Note' : 'Create HR Note'}
                            </h3>
                            <button
                                onClick={() => {
                                    setShowFormModal(false);
                                    resetForm();
                                }}
                                className="p-1.5 hover:bg-gray-200 rounded text-gray-500 font-bold transition-colors"
                            >
                                ✕
                            </button>
                        </div>

                        {/* Body - Scrollable Form */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            {/* Note Title */}
                            <div>
                                <label className="block text-gray-700 font-semibold mb-1">Note Title *</label>
                                <input
                                    type="text"
                                    value={formData.title}
                                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                                    placeholder="Enter announcement title"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-main focus:border-main bg-white text-gray-800"
                                />
                            </div>

                            {/* Note Description */}
                            <div>
                                <label className="block text-gray-700 font-semibold mb-1">Note Description *</label>
                                <div className="border border-gray-300 rounded-md overflow-hidden bg-white" style={{ minHeight: '180px' }}>
                                    {showFormModal && (
                                        <ReactQuill
                                            key={formData.id || 'new-note'}
                                            value={formData.description}
                                            onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
                                            placeholder="Enter announcement body..."
                                            theme="snow"
                                            modules={quillModules}
                                            className="custom-quill bg-white"
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Priority */}
                            <div>
                                <label className="block text-gray-700 font-semibold mb-1">Priority *</label>
                                <select
                                    value={formData.priority}
                                    onChange={(e) => setFormData(prev => ({ ...prev, priority: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-main focus:border-main bg-white text-gray-800"
                                >
                                    <option value="" disabled>Select priority</option>
                                    <option value="Normal">Normal</option>
                                    <option value="Important">Important</option>
                                    <option value="Critical">Critical</option>
                                </select>
                            </div>

                            {/* Message Type & Category */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-1">Message Type *</label>
                                    <select
                                        value={formData.message_type}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setFormData(prev => ({ ...prev, message_type: val }));
                                            if (val !== 'Individual Employee') {
                                                setSelectedEmployee(null);
                                            }
                                        }}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-main focus:border-main bg-white text-gray-800"
                                    >
                                        <option value="" disabled>Select message type</option>
                                        <option value="Global Announcement">Global Announcement</option>
                                        <option value="Individual Employee">Individual Employee</option>
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-1">Category *</label>
                                    <select
                                        value={formData.category}
                                        onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-main focus:border-main bg-white text-gray-800"
                                    >
                                        <option value="" disabled>Select category</option>
                                        <option value="memo">Memo</option>
                                        <option value="notification">Notification</option>
                                        <option value="event">Event</option>
                                        <option value="celebration">Celebration</option>
                                    </select>
                                </div>
                            </div>

                            {/* Searchable employee selection */}
                            {formData.message_type === 'Individual Employee' && (
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-1">Employee Selection *</label>
                                    <Select
                                        value={selectedEmployee}
                                        onChange={setSelectedEmployee}
                                        options={employees}
                                        placeholder="Search & select employee..."
                                        isSearchable
                                        isMulti
                                        className="react-select-container text-gray-800"
                                        classNamePrefix="react-select"
                                    />
                                </div>
                            )}

                            {/* Dates */}
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-1 text-xs">Effective Date</label>
                                    <input
                                        type="date"
                                        value={formData.effective_date}
                                        onChange={(e) => setFormData(prev => ({ ...prev, effective_date: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none bg-white text-gray-800 text-xs"
                                    />
                                </div>
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-1 text-xs">Expiry Date</label>
                                    <input
                                        type="date"
                                        value={formData.expiry_date}
                                        onChange={(e) => setFormData(prev => ({ ...prev, expiry_date: e.target.value }))}
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none bg-white text-gray-800 text-xs"
                                    />
                                </div>
                            </div>

                            {/* Attachment Upload */}
                            <div>
                                <label className="block text-gray-700 font-semibold mb-1 text-xs">Attachment (Optional)</label>
                                {attachment ? (
                                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-md border border-gray-200 text-xs text-gray-700">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <FiFileText className="text-gray-500 shrink-0 text-base" />
                                            <span className="truncate font-medium">{attachment.name}</span>
                                            <span className="text-[10px] text-gray-400 shrink-0">
                                                ({(attachment.size / 1024).toFixed(1)} KB)
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setAttachment(null);
                                                const fileInput = document.getElementById('note-attachment-input');
                                                if (fileInput) fileInput.value = '';
                                            }}
                                            className="p-1 hover:bg-gray-200 rounded text-red-500 hover:text-red-700 transition"
                                            title="Remove attachment"
                                        >
                                            <FiX className="text-base" />
                                        </button>
                                    </div>
                                ) : formData.existingAttachmentPath && !formData.deleteExistingAttachment ? (
                                    <div className="flex items-center justify-between p-2 bg-gray-50 rounded-md border border-gray-200 text-xs text-gray-700">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <FiFileText className="text-gray-500 shrink-0 text-base" />
                                            <a
                                                href={`${process.env.REACT_APP_IMAGEURL || ''}/${formData.existingAttachmentPath}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="truncate font-medium text-main hover:underline"
                                            >
                                                {formData.existingAttachmentName || 'Existing Attachment'}
                                            </a>
                                            <span className="text-[10px] text-gray-400 shrink-0">(Saved)</span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setFormData(prev => ({ ...prev, deleteExistingAttachment: true }));
                                            }}
                                            className="p-1 hover:bg-gray-200 rounded text-red-500 hover:text-red-700 transition"
                                            title="Delete existing attachment"
                                        >
                                            <FiX className="text-base" />
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        <input
                                            id="note-attachment-input"
                                            type="file"
                                            onChange={(e) => setAttachment(e.target.files[0] || null)}
                                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                            className="w-full text-xs text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 cursor-pointer"
                                        />
                                        <p className="text-[10px] text-gray-400 mt-1">Allowed: PDF, DOC, DOCX, PNG, JPG</p>
                                    </>
                                )}
                            </div>

                            {/* Require Acknowledgement Toggle */}
                            <div className="flex items-center justify-between p-2.5 bg-gray-50 rounded-lg border border-gray-100">
                                <div>
                                    <p className="font-semibold text-gray-700 text-xs">Require Acknowledgement</p>
                                    <p className="text-[10px] text-gray-400">Force popup until clicked</p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, require_acknowledgement: prev.require_acknowledgement === 1 ? 0 : 1 }))}
                                    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${formData.require_acknowledgement === 1 ? 'bg-main' : 'bg-gray-200'
                                        }`}
                                >
                                    <span
                                        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${formData.require_acknowledgement === 1 ? 'translate-x-5' : 'translate-x-0'
                                            }`}
                                    />
                                </button>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowFormModal(false);
                                    resetForm();
                                }}
                                className="py-2.5 px-4 bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold rounded-lg transition text-xs"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={submitting}
                                onClick={() => handleFormSubmit('Draft')}
                                className="py-2.5 px-4 bg-gray-100 hover:bg-gray-200 text-gray-800 font-bold rounded-lg transition text-xs border border-gray-200"
                            >
                                Save Draft
                            </button>
                            <button
                                type="button"
                                disabled={submitting}
                                onClick={() => handleFormSubmit('Published')}
                                className="py-2.5 px-4 bg-main hover:bg-mainHover text-white font-bold rounded-lg transition text-xs flex items-center justify-center gap-1"
                            >
                                {submitting ? 'Processing...' : (formData.id ? 'Update Note' : 'Publish Now')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Note Preview Popup Modal (Replay) */}
            {previewNote && (
                <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-[650px] overflow-hidden text-sm flex flex-col max-h-[85vh]">

                        {/* Header */}
                        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                            <span className="flex items-center gap-2 font-bold text-gray-800 text-base">
                                📢 HR Announcement Preview (Replay)
                            </span>
                            <div className="flex items-center gap-1.5">
                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${previewNote.priority === 'Critical'
                                    ? 'bg-red-100 text-red-800'
                                    : (previewNote.priority === 'Important' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800')
                                    }`}>
                                    {previewNote.priority}
                                </span>
                                {previewNote.message_type === 'Individual Employee' && (
                                    <span className="bg-indigo-100 text-indigo-800 px-2.5 py-0.5 rounded-full text-xs font-bold flex items-center gap-0.5">
                                        👤 Personal
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            <h3 className="text-lg font-extrabold text-gray-900">{previewNote.title}</h3>
                            <div
                                className="text-gray-700 leading-relaxed break-words prose prose-sm max-w-none border-b border-gray-100 pb-4"
                                dangerouslySetInnerHTML={{ __html: previewNote.description }}
                            />

                            {previewNote.attachment_path && (
                                <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <FiTag className="text-gray-400" />
                                        <span className="text-xs text-gray-600 truncate max-w-[280px]">
                                            {previewNote.attachment_name || 'Attachment'}
                                        </span>
                                    </div>
                                    <a
                                        href={`${process.env.REACT_APP_IMAGEURL || ''}/${previewNote.attachment_path}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-xs text-main hover:text-mainHover font-bold flex items-center gap-1 hover:underline"
                                    >
                                        <FiDownload className="inline-block mr-1" /> Download
                                    </a>
                                </div>
                            )}

                            <div className="text-[10px] text-gray-400 flex justify-between items-center bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                                <span>Author: <strong className="text-gray-600">{previewNote.author_name || 'HR Department'}</strong></span>
                                <span>Posted: <strong className="text-gray-600">{new Date(previewNote.created_at).toLocaleString('en-GB')}</strong></span>
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="px-6 py-4 border-t border-gray-200 flex justify-end bg-gray-50">
                            <button
                                onClick={() => setPreviewNote(null)}
                                className="px-5 py-2 bg-gray-700 hover:bg-gray-800 text-white font-bold rounded-lg transition text-xs"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Custom Delete Confirm Modal */}
            {confirmDeleteId && (
                <div className="fixed inset-0 bg-black/50 z-[200] flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6 text-center space-y-4 animate-fade-in">
                        <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                            <FiTrash2 className="text-red-600 text-xl" />
                        </div>
                        <h3 className="font-bold text-gray-800 text-base">Delete HR Note</h3>
                        <p className="text-sm text-gray-500">Are you sure you want to delete this note? This action cannot be undone.</p>
                        <div className="flex gap-3 justify-center pt-1">
                            <button
                                onClick={() => setConfirmDeleteId(null)}
                                className="px-5 py-2 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-gray-50 transition text-sm"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmDelete}
                                className="px-5 py-2 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition text-sm flex items-center gap-1.5"
                            >
                                <FiTrash2 size={13} /> Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}

// =========================================================================
// SUB-COMPONENT: HR Acknowledgement Tracking Screen (Full Table with Filters)
// =========================================================================
function HrAcknowledgementTracking() {
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchText, setSearchText] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [filters, setFilters] = useState({
        employee: '',
        department: '',
        note_title: '',
        status: '',
        start_date: '',
        end_date: ''
    });

    const [employees, setEmployees] = useState([]);
    const [notesList, setNotesList] = useState([]);

    const reactSelectCustomStyles = useMemo(() => ({
        control: (provided, state) => ({
            ...provided,
            minHeight: '28px',
            height: '28px',
            borderRadius: '0.5rem',
            borderColor: state.isFocused ? '#C30010' : '#d1d5db',
            boxShadow: state.isFocused ? '0 0 0 1px #C30010' : 'none',
            fontSize: '0.75rem',
            backgroundColor: '#ffffff',
            '&:hover': {
                borderColor: state.isFocused ? '#C30010' : '#d1d5db'
            }
        }),
        valueContainer: (provided) => ({
            ...provided,
            height: '28px',
            padding: '0 8px',
            display: 'flex',
            alignItems: 'center'
        }),
        input: (provided) => ({
            ...provided,
            margin: '0px',
            padding: '0px',
        }),
        indicatorsContainer: (provided) => ({
            ...provided,
            height: '28px',
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            padding: '2px',
        }),
        clearIndicator: (provided) => ({
            ...provided,
            padding: '2px',
        }),
        option: (provided, state) => ({
            ...provided,
            fontSize: '0.75rem',
            backgroundColor: state.isSelected ? '#C30010' : state.isFocused ? '#f3f4f6' : '#ffffff',
            color: state.isSelected ? '#ffffff' : '#1f2937',
            cursor: 'pointer',
        }),
        menu: (provided) => ({
            ...provided,
            borderRadius: '0.5rem',
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
            zIndex: 50
        })
    }), []);

    const employeeOptions = useMemo(() => {
        return employees.map(emp => ({
            value: emp.emp_id || emp.name,
            label: emp.name
        }));
    }, [employees]);

    const fetchDropdownData = async () => {
        try {
            const [empRes, notesRes] = await Promise.all([
                api.get('/hrms/employee-list'),
                api.get('/hrms/notes/list')
            ]);
            if (empRes.data.status) {
                const list = empRes.data.data
                    .filter(emp => parseInt(emp.account_status ?? emp.status ?? 0) === 1)
                    .map(emp => ({
                        id: emp.emp_id || emp.user_id,
                        name: `${emp.first_name} ${emp.last_name}`,
                        emp_id: emp.emp_id
                    }));
                list.sort((a, b) => a.name.localeCompare(b.name));
                setEmployees(list);
            }
            if (notesRes.data.status) {
                setNotesList(notesRes.data.data || []);
            }
        } catch (e) {
            console.error('Error fetching tracking dropdown data:', e);
        }
    };

    const fetchReport = async () => {
        setLoading(true);
        try {
            const res = await api.get('/hrms/notes/acknowledgements', { params: filters });
            if (res.data.status) setRecords(res.data.data);
        } catch (e) {
            console.error('Error fetching ack report:', e);
            toast.error('Failed to load tracking report');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDropdownData();
    }, []);

    useEffect(() => {
        fetchReport();
    }, [filters]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const filteredRecords = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        return records.filter((row) => {
            if (!query) return true;
            return ['employee_name', 'department', 'note_title', 'message_type', 'status'].some((field) =>
                String(row[field] ?? '').toLowerCase().includes(query)
            );
        });
    }, [records, searchText]);

    const groupedNotes = useMemo(() => {
        const groups = {};
        filteredRecords.forEach(row => {
            const noteId = row.note_id;
            if (!groups[noteId]) {
                groups[noteId] = {
                    id: noteId,
                    title: row.note_title,
                    message_type: row.message_type,
                    sent_date: row.sent_date,
                    recipients: [],
                    read_count: 0,
                    acknowledged_count: 0,
                    pending_count: 0,
                    total_recipients: 0
                };
            }
            groups[noteId].recipients.push(row);
            groups[noteId].total_recipients++;
            if (row.status === 'Read') {
                groups[noteId].read_count++;
            }
            if (row.status === 'Acknowledged') {
                groups[noteId].acknowledged_count++;
            } else {
                groups[noteId].pending_count++;
            }
        });
        return Object.values(groups);
    }, [filteredRecords]);

    const parentColumns = useMemo(() => [
        {
            name: 'Note Title',
            selector: row => row.title,
            sortable: true,
            minWidth: '220px',
            grow: 3,
            cell: row => <span className="text-gray-800 font-semibold text-xs truncate max-w-[300px]" title={row.title}>{row.title}</span>
        },
        {
            name: 'Type',
            selector: row => row.message_type,
            sortable: true,
            minWidth: '160px',
            grow: 2,
            cell: row => <span className="text-gray-500 text-xs">{row.message_type}</span>
        },
        {
            name: 'Sent Date',
            selector: row => row.sent_date || '',
            sortable: true,
            minWidth: '140px',
            grow: 1.5,
            cell: row => <span className="text-gray-500 text-xs">{row.sent_date ? new Date(row.sent_date).toLocaleDateString('en-GB') : '-'}</span>
        },
        {
            name: 'Stats (Read/Ack)',
            selector: row => row.acknowledged_count,
            sortable: true,
            minWidth: '180px',
            grow: 2,
            cell: row => (
                <div className="flex items-center gap-1.5 text-[10px] font-bold">
                    <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full" title="Read Count">R: {row.read_count}</span>
                    <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full" title="Acknowledged Count">A: {row.acknowledged_count}</span>
                    <span className="bg-orange-50 text-orange-700 px-2 py-0.5 rounded-full" title="Pending Count">P: {row.pending_count}</span>
                </div>
            )
        }
    ], []);

    const ExpandedRowComponent = ({ data }) => {
        const [nestedSearchText, setNestedSearchText] = useState('');
        const [nestedStatus, setNestedStatus] = useState('');
        const [nestedRowsPerPage, setNestedRowsPerPage] = useState(10);

        const filteredRecipients = useMemo(() => {
            const query = nestedSearchText.trim().toLowerCase();
            return data.recipients.filter((row) => {
                const matchesSearch = !query || ['employee_name', 'department'].some((field) =>
                    String(row[field] ?? '').toLowerCase().includes(query)
                );
                const matchesStatus = !nestedStatus || String(row.status).toLowerCase() === nestedStatus.toLowerCase();
                return matchesSearch && matchesStatus;
            });
        }, [data.recipients, nestedSearchText, nestedStatus]);

        const nestedColumns = [
            {
                name: 'Employee Name',
                selector: row => row.employee_name,
                sortable: true,
                minWidth: '180px',
                grow: 2,
                cell: row => <span className="text-gray-800 font-semibold text-xs">{row.employee_name}</span>
            },
            {
                name: 'Department',
                selector: row => row.department || '',
                sortable: true,
                minWidth: '160px',
                grow: 2,
                cell: row => <span className="text-gray-600 text-xs">{row.department || '-'}</span>
            },
            {
                name: 'Read Date',
                selector: row => row.read_date || '',
                sortable: true,
                minWidth: '160px',
                grow: 1.5,
                cell: row => <span className="text-gray-500 text-xs whitespace-nowrap">{row.read_date ? new Date(row.read_date).toLocaleString('en-GB') : '-'}</span>
            },
            {
                name: 'Ack Date',
                selector: row => row.acknowledged_date || '',
                sortable: true,
                minWidth: '160px',
                grow: 1.5,
                cell: row => <span className="text-gray-500 text-xs whitespace-nowrap">{row.acknowledged_date ? new Date(row.acknowledged_date).toLocaleString('en-GB') : '-'}</span>
            },
            {
                name: 'Status',
                selector: row => row.status,
                sortable: true,
                minWidth: '140px',
                grow: 1.2,
                center: true,
                cell: row => (
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider whitespace-nowrap ${row.status === 'Acknowledged'
                        ? 'bg-green-100 text-green-800'
                        : (row.status === 'Read' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800')
                        }`}>
                        {row.status}
                    </span>
                )
            }
        ];

        return (
            <div className="bg-gray-50 p-6 border-l-4 border-main rounded-r-lg shadow-inner space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wider flex items-center gap-1.5">
                        👥 Recipient Tracking for "{data.title}" ({data.recipients.length} employees)
                    </h4>

                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500 font-medium">
                            <span>Show</span>
                            <select
                                value={nestedRowsPerPage}
                                onChange={(e) => setNestedRowsPerPage(Number(e.target.value))}
                                className="bg-white border border-gray-200 rounded-lg pl-2 pr-6 py-1 text-xs font-bold text-gray-700 focus:ring-1 focus:ring-main outline-none cursor-pointer min-w-[55px]"
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                            <span>entries</span>
                        </div>
                        <div className="relative">
                            <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                            <input
                                type="text"
                                value={nestedSearchText}
                                onChange={(e) => setNestedSearchText(e.target.value)}
                                placeholder="Search employee..."
                                className="pl-8 pr-7 py-1 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-main w-40 bg-white"
                            />
                            {nestedSearchText && (
                                <button
                                    onClick={() => setNestedSearchText('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <FiX size={12} />
                                </button>
                            )}
                        </div>
                        <select
                            value={nestedStatus}
                            onChange={(e) => setNestedStatus(e.target.value)}
                            className="bg-white border border-gray-200 rounded-lg pl-2 pr-6 py-1 text-xs font-bold text-gray-700 focus:ring-1 focus:ring-main outline-none cursor-pointer"
                        >
                            <option value="">All Statuses</option>
                            <option value="Pending">Pending</option>
                            <option value="Read">Read</option>
                            <option value="Acknowledged">Acknowledged</option>
                        </select>
                    </div>
                </div>

                <div className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm">
                    <DataTable
                        key={nestedRowsPerPage}
                        columns={nestedColumns}
                        data={filteredRecipients}
                        noHeader
                        pagination
                        paginationComponent={CustomPagination}
                        paginationPerPage={nestedRowsPerPage}
                        paginationRowsPerPageOptions={[5, 10, 25, 50]}
                        customStyles={customStyles}
                        highlightOnHover
                    />
                </div>
            </div>
        );
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-gray-100 pb-3">
                <h2 className="font-bold text-gray-800 text-lg flex items-center gap-2">
                    <FiUsers className="text-main" /> Note Acknowledgement Tracking
                </h2>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <span>Show</span>
                        <select
                            value={rowsPerPage}
                            onChange={(e) => setRowsPerPage(Number(e.target.value))}
                            className="bg-white border border-gray-200 rounded-lg pl-2 pr-6 py-1 text-xs font-bold text-gray-700 focus:ring-1 focus:ring-main outline-none cursor-pointer min-w-[55px]"
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <span>entries</span>
                    </div>
                    <div className="relative">
                        <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                        <input
                            type="text"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            placeholder="Quick search table..."
                            className="pl-8 pr-7 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-main w-52 bg-white"
                        />
                        {searchText && <button onClick={() => setSearchText('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><FiX size={12} /></button>}
                    </div>
                </div>
            </div>

            {/* Data table */}
            <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm bg-white">
                <DataTable
                    key={rowsPerPage}
                    columns={parentColumns}
                    data={groupedNotes}
                    expandableRows
                    expandableRowsComponent={ExpandedRowComponent}
                    customStyles={customStyles}
                    progressPending={loading}
                    progressComponent={<div className="flex justify-center items-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-main"></div></div>}
                    pagination
                    paginationComponent={CustomPagination}
                    paginationPerPage={rowsPerPage}
                    paginationRowsPerPageOptions={[5, 10, 25, 50, 100]}
                    highlightOnHover
                    noDataComponent={
                        <div className="px-6 py-8 text-center text-gray-500">
                            No acknowledgement records match the filters.
                        </div>
                    }
                />
            </div>
        </div>
    );
}

// =========================================================================
// SUB-COMPONENT: Employee Notes View (Lists and Details)
// =========================================================================
function EmployeeNotesView() {
    const { user } = useUser();
    const roleId = parseInt(user?.user_role_id ?? user?.role_id ?? 0);
    const isAdmin = roleId === 1;
    const [notesList, setNotesList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewingNote, setViewingNote] = useState(null);
    const [ackSuccess, setAckSuccess] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const fetchMyNotes = async () => {
        setLoading(true);
        try {
            const res = await api.get('/hrms/notes/active');
            if (res.data.status) {
                setNotesList(res.data.data.recent || []);
            }
        } catch (e) {
            console.error('Error fetching employee notes:', e);
            toast.error('Failed to load notes');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMyNotes();
    }, []);

    const handleOpenNote = async (note) => {
        setViewingNote(note);
        setAckSuccess(false);

        // Mark note as Read in backend
        if (note.read_status === 'Unread' || note.read_status === 'Pending') {
            try {
                await api.post('/hrms/notes/read', { note_id: note.id });
                // Proactively update UI read status
                setNotesList(prev => prev.map(n => n.id === note.id ? { ...n, read_status: 'Read' } : n));
            } catch (e) { console.error('Error marking note read:', e); }
        }
    };

    const handleAcknowledge = async () => {
        if (!viewingNote) return;
        try {
            const res = await api.post('/hrms/notes/acknowledge', {
                note_id: viewingNote.id
            });
            if (res.data.status) {
                setAckSuccess(true);
                toast.success('Note acknowledged successfully');
                // Proactively update list item status
                setNotesList(prev => prev.map(n => n.id === viewingNote.id ? { ...n, read_status: 'Acknowledged' } : n));

                // Auto close popup details after 1.5 seconds
                setTimeout(() => {
                    setViewingNote(null);
                    setAckSuccess(false);
                }, 1500);
            }
        } catch (e) {
            console.error('Acknowledge note error:', e);
            toast.error('Error acknowledging note');
        }
    };

    const filteredNotes = useMemo(() => {
        const query = searchText.trim().toLowerCase();
        return notesList.filter((row) => {
            if (!query) return true;
            return ['title', 'priority', 'message_type', 'read_status', 'author_name'].some((field) =>
                String(row[field] ?? '').toLowerCase().includes(query)
            );
        });
    }, [notesList, searchText]);

    const columns = useMemo(() => [
        {
            name: 'Title',
            selector: row => row.title,
            sortable: true,
            minWidth: '220px',
            grow: 3,
            cell: row => (
                <div className="font-medium text-gray-800 py-2">
                    <div className="truncate text-xs font-semibold text-gray-800" title={row.title}>{row.title}</div>
                    {row.message_type === 'Individual Employee' && (
                        <div className="text-[9px] text-indigo-600 font-bold mt-0.5">👤 Personal</div>
                    )}
                </div>
            )
        },
        {
            name: 'Priority',
            selector: row => row.priority,
            sortable: true,
            minWidth: '120px',
            grow: 1.2,
            cell: row => {
                const pColor = priorityColors[row.priority] || priorityColors['Normal'];
                return (
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${pColor.badge}`}>
                        {row.priority}
                    </span>
                );
            }
        },
        {
            name: 'Type',
            selector: row => row.message_type,
            sortable: true,
            minWidth: '160px',
            grow: 2,
            cell: row => <span className="text-gray-500 text-xs">{row.message_type}</span>
        },
        {
            name: 'Posted',
            selector: row => row.created_at,
            sortable: true,
            minWidth: '120px',
            grow: 1.2,
            cell: row => <span className="text-gray-500 text-xs">{new Date(row.created_at).toLocaleDateString('en-GB')}</span>
        },
        {
            name: 'Status',
            selector: row => row.read_status,
            sortable: true,
            minWidth: '130px',
            grow: 1.2,
            cell: row => (
                <span className={`px-2 py-0.5 rounded font-bold uppercase tracking-wider text-[9px] whitespace-nowrap ${row.read_status === 'Acknowledged' ? 'bg-green-100 text-green-800'
                    : row.read_status === 'Read' ? 'bg-blue-100 text-blue-800'
                        : 'bg-red-100 text-red-800 animate-pulse'
                    }`}>
                    {row.read_status}
                </span>
            )
        },
        {
            name: 'Actions',
            minWidth: '120px',
            grow: 1.2,
            center: true,
            cell: row => (
                <div onClick={e => e.stopPropagation()}>
                    <button
                        onClick={(e) => { e.stopPropagation(); handleOpenNote(row); }}
                        className="flex items-center gap-1 text-xs text-main hover:bg-main/10 px-2 py-1 rounded font-semibold transition"
                    >
                        <FiEye size={12} /> View
                    </button>
                </div>
            )
        }
    ], []);

    if (loading) {
        return (
            <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-main"></div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-gray-100 pb-3">
                <h2 className="font-bold text-gray-800 text-lg flex items-center gap-2">
                    📢 HR Announcements & Personal Notes
                </h2>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-1.5 text-xs text-gray-500">
                        <span>Show</span>
                        <select
                            value={rowsPerPage}
                            onChange={(e) => setRowsPerPage(Number(e.target.value))}
                            className="bg-white border border-gray-200 rounded-lg pl-2 pr-6 py-1 text-xs font-bold text-gray-700 focus:ring-1 focus:ring-main outline-none cursor-pointer min-w-[55px]"
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </select>
                        <span>entries</span>
                    </div>
                    <div className="relative">
                        <FiSearch className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-xs" />
                        <input
                            type="text"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            placeholder="Search announcements..."
                            className="pl-8 pr-7 py-1.5 text-xs border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-main w-52"
                        />
                        {searchText && <button onClick={() => setSearchText('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"><FiX size={12} /></button>}
                    </div>
                </div>
            </div>

            {/* Notes Table */}
            <div className="border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                <div className="border-t border-gray-100 bg-white">
                    <DataTable
                        columns={columns}
                        data={filteredNotes}
                        customStyles={customStyles}
                        pagination
                        paginationComponent={CustomPagination}
                        paginationPerPage={rowsPerPage}
                        paginationRowsPerPageOptions={[5, 10, 25, 50, 100]}
                        highlightOnHover
                        pointerOnHover
                        onRowClicked={row => handleOpenNote(row)}
                        noDataComponent={
                            <div className="px-6 py-10 text-center text-gray-400 text-xs">
                                {searchText ? 'No announcements match your search.' : 'No active announcements or notes available for you.'}
                            </div>
                        }
                    />
                </div>
            </div>


            {/* Modal Detail & Acknowledge Popup */}
            {viewingNote && (
                <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-[650px] overflow-hidden text-sm flex flex-col max-h-[85vh]">

                        {/* Header */}
                        <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                            <span className="flex items-center gap-2 font-bold text-gray-800 text-base">
                                📢 HR Announcement
                            </span>
                            <div className="flex items-center gap-1.5">
                                <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${priorityColors[viewingNote.priority]?.badge
                                    }`}>
                                    {viewingNote.priority}
                                </span>
                                {viewingNote.message_type === 'Individual Employee' && (
                                    <span className="bg-indigo-100 text-indigo-800 px-2.5 py-0.5 rounded-full text-xs font-bold flex items-center gap-0.5">
                                        👤 Personal
                                    </span>
                                )}
                                {viewingNote.category && (
                                    <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold capitalize ${viewingNote.category === 'celebration' ? 'bg-pink-100 text-pink-800' :
                                        viewingNote.category === 'event' ? 'bg-purple-100 text-purple-800' :
                                            viewingNote.category === 'notification' ? 'bg-amber-100 text-amber-800' :
                                                'bg-teal-100 text-teal-800'
                                        }`}>
                                        {viewingNote.category}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-4">
                            <h3 className="text-lg font-extrabold text-gray-900">{viewingNote.title}</h3>
                            <div
                                className="text-gray-700 leading-relaxed break-words prose prose-sm max-w-none border-b border-gray-100 pb-4"
                                dangerouslySetInnerHTML={{ __html: viewingNote.description }}
                            />

                            {viewingNote.attachment_path && (
                                <div className="p-3 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <FiTag className="text-gray-400" />
                                        <span className="text-xs text-gray-600 truncate max-w-[280px]">
                                            {viewingNote.attachment_name || 'Attachment'}
                                        </span>
                                    </div>
                                    <a
                                        href={`${process.env.REACT_APP_IMAGEURL || ''}/${viewingNote.attachment_path}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="text-xs text-main hover:text-mainHover font-bold flex items-center gap-1 hover:underline"
                                    >
                                        <FiDownload /> Download
                                    </a>
                                </div>
                            )}

                            <div className="text-[10px] text-gray-400 flex justify-between items-center bg-gray-50 p-2.5 rounded-lg border border-gray-100">
                                <span>Author: <strong className="text-gray-600">{viewingNote.author_name || 'HR Department'}</strong></span>
                                <span>Posted: <strong className="text-gray-600">{new Date(viewingNote.created_at).toLocaleString('en-GB')}</strong></span>
                            </div>


                        </div>

                        {/* Footer / Acknowledge buttons */}
                        <div className="px-6 py-4 border-t border-gray-200 flex justify-end items-center bg-gray-50">
                            {ackSuccess ? (
                                <div className="w-full text-center text-green-700 font-extrabold flex items-center justify-center gap-1 text-sm bg-green-50 py-2 rounded-lg border border-green-200">
                                    <FiCheck /> ✅ Acknowledged Successfully
                                </div>
                            ) : (
                                <>
                                    {parseInt(viewingNote.require_acknowledgement) === 1 && viewingNote.read_status !== 'Acknowledged' && !isAdmin ? (
                                        <button
                                            onClick={handleAcknowledge}
                                            className="px-5 py-2 bg-main hover:bg-mainHover text-white font-extrabold rounded-lg transition text-xs flex items-center gap-1"
                                        >
                                            ✓ Acknowledge
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => setViewingNote(null)}
                                            className="px-5 py-2 bg-gray-700 hover:bg-gray-800 text-white font-bold rounded-lg transition text-xs"
                                        >
                                            Close
                                        </button>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>

    );
}

